import * as React from 'react'
import JSZip from 'jszip';
import { FaDownload } from 'react-icons/fa';
import { downloadRTTemplate, fetchKitData } from '../utility';

const ACCEPTED_DATA_FILE_EXTENSIONS = ['raw', 'wiff', 'mzML', 'd', 'json']

const FILE_VALIDATIONS = {
    file_groups: { cal: 'Cal_cal', blank: 'Blank_blk', double_blank: 'Double_Blank_dbl_blk', qc: 'QC_qc', experimental: 'Exp', },
    "DFI 1": {
        counts: { cal: 7, blank: 3, double_blank: 3, qc: 3, },
    },

}

const ASSAYS = {
    MEGA: ["DFI 1", "DFI 2", "Panel A", "Panel B"],
    PRIME: ["DFI 1", "DFI 2", "Panel A", "Panel B"],
    GIGA: ["DFI 1", "DFI 2", "DFI 3", "DFI 4", "Panel A", "Panel B"],
}



export default SubmissionZip = () => {

    const PROJECT_ID = new URLSearchParams(window.location.search).get('project_id')
    const KIT_REGISTRATION_ID = new URLSearchParams(window.location.search).get('kit_registration_id')


    const dropZoneRef = React.useRef(null);
    const [isDragOver, setIsDragOver] = React.useState(false);
    const [zipFile, setZipFile] = React.useState(null)
    const [zipFileContents, setZipFileContents] = React.useState([])
    const [zipFileNames, setZipFileNames] = React.useState({ file_names: [], rt_rows: {} })
    const [selectedPanel, setSelectedPanel] = React.useState('DFI 1')
    const [rtMin, setRtMin] = React.useState(0.0)
    const [rtMax, setRtMax] = React.useState(1.0)
    const [submissionName, setSubmissionName] = React.useState('')
    const [useSmartPeakPicking, setUseSmartPeakPicking] = React.useState(true)
    const [instrument, setInstrument] = React.useState(null)
    const [biofluid, setBiofluid] = React.useState(null)
    const [assay, setAssay] = React.useState(null)
    const [enablePreembeddedLODs, setEnablePreembeddedLODs] = React.useState(false)

    const [areaToUse, setAreaToUse] = React.useState('area_vertical_raw')
    const [peakProcessToUse, setPeakProcessToUse] = React.useState('simple')
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [parsingFiles, setParsingFiles] = React.useState(false)
    const [lodMethod, setLodMethod] = React.useState("use_dynamic_lods")


    console.log("HEREHREHREH")

    React.useEffect(() => {
        setSelectedPanel(ASSAYS.MEGA[2])

        fetchKitData(new URLSearchParams(window.location.search).get('kit_registration_id'))
            .then((data) => {
                console.log("kit data", data)
                setInstrument(data.instrument)
                setBiofluid(data.biofluid)
                setAssay(data.assay)
                if (data.assay === "MEGA") {
                    setEnablePreembeddedLODs(true)
                }
                if (data.assay === "GIGA" && data.biofluid.toLowerCase() === "blood" && !["Panel B"].includes(selectedPanel)) {
                    setEnablePreembeddedLODs(true)
                    console.log("GIGA blood detected, enabling pre-embedded LODs")
                }
            })

        return () => {};

    }, [])

    React.useEffect(() => {
        if (selectedPanel == "Panel B" && assay === "GIGA" && biofluid?.toLowerCase() === "blood" && lodMethod === "use_preembedded_lods") {
            setEnablePreembeddedLODs(false)
            setLodMethod("use_dynamic_lods")
            alert("Pre-embedded LODs are not available for GIGA Panel B. Dynamic LODs will be used instead.")
        }
    }, [selectedPanel, assay, biofluid, lodMethod])

    // Original event listener removed — was FilePond-based

    function handleFileSelected(file) {
        setZipFile(file);
        const onProgress = (progress) => {
            setUploadProgress(progress);
        };
        const onComplete = (data) => {
            console.log('Upload complete:', data);
            setZipFileNames(data.file_names);
            setParsingFiles(false); // parsing is now synchronous via useMemo
        };
        const onError = (error) => {
            console.error(error);
        };
        uploadFileWithProgress(file, onProgress, onComplete, onError, PROJECT_ID, KIT_REGISTRATION_ID, selectedPanel);
    }

    function uploadFileWithProgress(file, onProgress, onComplete, onError, project_id,
        kit_registration_id, selected_panel) {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        formData.append('zip_file', file);
        formData.append('project_id', project_id);
        formData.append('kit_registration_id', kit_registration_id);
        formData.append('selected_panel', selected_panel);
        // Append other form data here if needed

        xhr.open('POST', '/validate', true);
        xhr.timeout = 10800000 // 3 hours timeout (for Orbitrap files)
        // Set CSRF token
        const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");
        xhr.setRequestHeader('X-CSRF-Token', csrfToken);

        // Event listener for when the request completes
        xhr.onload = function () {
            if (xhr.status === 200) {
                onComplete(JSON.parse(xhr.responseText));
            } else {
                onError('Upload failed: ' + xhr.statusText);
            }
        };

        // Event listener for errors in the request
        xhr.onerror = function () {
            onError('Upload failed. Please try again.');
        };

        // Event listener for progress events
        xhr.upload.onprogress = function (event) {
            if (event.lengthComputable) {
                const percentComplete = event.loaded / event.total * 100;
                onProgress(percentComplete);
                if (percentComplete === 100) {
                    setParsingFiles(true)
                }
            }
        };

        xhr.send(formData);
    }

    // Reset upload state instantly when zip file is cleared
    React.useEffect(() => {
        if (!zipFile) {
            setZipFileNames({ file_names: [], rt_rows: {} })
            setUploadProgress(0)
            setParsingFiles(false)
        }
    }, [zipFile])

    // Derive all file categories and flags synchronously — no extra render cycle needed
    const {
        calFiles, blankFiles, doubleBlankFiles, qcFiles, experimentalFiles,
        rtFile, unacceptedFiles, fileFlags
    } = React.useMemo(() => {
        const empty = {
            calFiles: [], blankFiles: [], doubleBlankFiles: [], qcFiles: [],
            experimentalFiles: [], rtFile: null, unacceptedFiles: [], fileFlags: []
        }
        if (!zipFileNames || zipFileNames.file_names.length === 0) return empty

        let fileNames = [...zipFileNames.file_names] // clone to avoid mutation
        let rtFlags = []
        let rtFileFound = null

        if (["Panel A", "Panel B"].includes(selectedPanel)) {
            const rtFiles = detectRTFile(fileNames)
            if (rtFiles.length === 1) {
                rtFileFound = rtFiles[0]
                rtFlags = validateFileName(rtFiles[0], PROJECT_ID, KIT_REGISTRATION_ID, true, instrument, biofluid, assay, selectedPanel)
                rtFlags = [...rtFlags, ...validateRTRows(zipFileNames.rt_rows)]
                fileNames.splice(fileNames.indexOf(rtFiles[0]), 1)
            } else {
                rtFlags = ['no or multiple rt files found']
            }
        }

        let updatedFlags = [...rtFlags]
        let improperFileNames = []
        fileNames.forEach((name) => {
            const flags = validateFileName(name, PROJECT_ID, KIT_REGISTRATION_ID, false)
            updatedFlags = [...updatedFlags, ...flags]
            if (flags.length > 0) improperFileNames.push(name)
        })

        const remainingFiles = fileNames.filter(f => !improperFileNames.includes(f))

        let calFiles = [], blankFiles = [], doubleBlankFiles = [], qcFiles = [],
            experimentalFiles = [], looseFiles = []

        remainingFiles.forEach((fileName) => {
            const fg = FILE_VALIDATIONS.file_groups
            const isCal = fileName.includes(fg.cal) && !fileName.includes(fg.blank) && !fileName.includes(fg.double_blank) && !fileName.includes(fg.qc) && !fileName.includes(fg.experimental)
            const isBlank = fileName.includes(fg.blank) && !fileName.includes(fg.cal) && !fileName.includes(fg.double_blank) && !fileName.includes(fg.qc) && !fileName.includes(fg.experimental)
            const isDbl = fileName.includes(fg.double_blank) && !fileName.includes(fg.cal) && !fileName.includes(fg.blank) && !fileName.includes(fg.qc) && !fileName.includes(fg.experimental)
            const isQc = fileName.includes(fg.qc) && !fileName.includes(fg.cal) && !fileName.includes(fg.blank) && !fileName.includes(fg.double_blank) && !fileName.includes(fg.experimental)
            const isExp = fileName.includes(fg.experimental) && !fileName.includes(fg.cal) && !fileName.includes(fg.blank) && !fileName.includes(fg.double_blank) && !fileName.includes(fg.qc)
            const isLoose = !isCal && !isBlank && !isDbl && !isQc && !isExp

            if (isCal) calFiles.push(fileName)
            if (isBlank) blankFiles.push(fileName)
            if (isDbl) doubleBlankFiles.push(fileName)
            if (isQc) qcFiles.push(fileName)
            if (isExp) experimentalFiles.push(fileName)
            if (isLoose) looseFiles.push(fileName)
        })

        if (looseFiles.length > 0) updatedFlags.push('unclassifiable files present')
        updatedFlags = [...new Set(updatedFlags)]

        return {
            calFiles, blankFiles, doubleBlankFiles, qcFiles, experimentalFiles,
            rtFile: rtFileFound,
            unacceptedFiles: [...improperFileNames, ...looseFiles],
            fileFlags: updatedFlags
        }
    }, [zipFileNames, selectedPanel, instrument, biofluid, assay])

    function confirmSubmission() {

        console.log("confirm submission")
        let formData = new FormData()
        // formData.append('zipFile', zipFile)

        let urlParams = new URLSearchParams(window.location.search)
        formData.append('project_id', urlParams.get('project_id'))
        formData.append('kit_registration_id', urlParams.get('kit_registration_id'))
        formData.append('rt_min', rtMin)
        formData.append('rt_max', rtMax)
        formData.append('submissionName', submissionName)
        // formData.append('use_smart_peak_picking', useSmartPeakPicking)
        formData.append('panel', selectedPanel)
        formData.append('area_to_use', areaToUse)
        formData.append('peak_process_to_use', peakProcessToUse)
        formData.append('lod_method', lodMethod)
        console.log("formData", formData)


        fetch('/confirm_submission', {
            method: 'POST',
            body: formData,
            headers: {
                'X-CSRF-Token': document.querySelector("meta[name=csrf-token]").content
            }
        })
            .then(response => response.json())
            .then((response) => {
                console.log("response", response)
                if (response.success === 1) {
                    window.location = `${window.location.origin}/kit_registrations/${new URLSearchParams(window.location.search).get('kit_registration_id')}`
                } else {
                    window.location = `${window.location.origin}/500.html`
                }
            })
    }

    return (
        <div className='sub-split-layout'>

            {/* LEFT COLUMN: Form */}
            <div className='sub-form-side'>
                <div className='kit-reg-card'>

                    {/* STEP 1: Submission Configuration */}
                    <div className='kit-reg-section'>
                        <h6 className='kit-reg-section-title'>
                            <i className='bi bi-1-circle me-2'></i>
                            Submission Configuration
                        </h6>
                        <div className='kit-reg-grid'>
                            <div className='kit-reg-field'>
                                <label className='kit-reg-label' htmlFor='submission-name'>Submission Name</label>
                                <input type='text' className='kit-reg-input' id='submission-name'
                                    placeholder='e.g. Plasma_Run_2026_02_18'
                                    onChange={(e) => {
                                        setSubmissionName(e.target.value)
                                    }}
                                    value={submissionName}
                                />
                            </div>
                            <div className='kit-reg-field'>
                                <label className='kit-reg-label' htmlFor='panel-select'>Panel</label>
                                <select className='kit-reg-select' id='panel-select' value={selectedPanel}
                                    onChange={(e) => {
                                        setSelectedPanel(e.target.value)
                                    }}
                                >
                                    {
                                        assay === "MEGA" &&
                                        ASSAYS.MEGA.map((panel, index) => {
                                            return (
                                                <option value={panel} key={`${index}-value-${panel}`}>{panel}</option>
                                            )
                                        }) ||
                                        assay === "PRIME" &&
                                        ASSAYS.PRIME.map((panel, index) => {
                                            return (
                                                <option value={panel} key={`${index}-value-${panel}`}>{panel}</option>
                                            )
                                        }) ||
                                        assay === "GIGA" &&
                                        ASSAYS.GIGA.map((panel, index) => {
                                            return (
                                                <option value={panel} key={`${index}-value-${panel}`}>{panel}</option>
                                            )
                                        })
                                    }
                                </select>
                            </div>
                            <div className='kit-reg-field'>
                                <label className='kit-reg-label' htmlFor='lod-select'>LOD Method</label>
                                <select className='kit-reg-select' id='lod-select' value={lodMethod}
                                    onChange={(e) => {
                                        setLodMethod(e.target.value)
                                    }}
                                >
                                    <option value='use_dynamic_lods'>Dynamic</option>
                                    <option value='use_preembedded_lods' disabled={!enablePreembeddedLODs}>Pre-Embedded</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    {/* <div className="form-check-reverse mb-3">
                <input className="form-check-input" 
                type="checkbox" 
                value="" 
                id="use-smart-peak-picking-checkbox" 
                checked={useSmartPeakPicking}
                onChange={(e) => setUseSmartPeakPicking(e.target.checked)}
                />
                <label className="form-check-label text-align-baseline" htmlFor="use-smart-peak-picking-checkbox">
                    Use smart peak picking
                </label>
            </div>

            <div className='form-check-reverse mb-3'>
                <label className='form-check-label text-align-baseline' htmlFor='area-to-use'>Peak Proccess to Use:</label>
                <div class="dropdown">
                    <button className="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                        {peakProcessToUse}
                    </button>
                    <ul className="dropdown-menu">
                        <li className="dropdown-item" value="simple"
                            onClick={(e) => {
                                setPeakProcessToUse(e.target.innerText)
                            }}
                        >simple</li>
                        <li className="dropdown-item" value="gmm"
                            onClick={(e) => {
                                // console.log("e.target.value", e.target.innerText)
                                setPeakProcessToUse(e.target.innerText)
                            }} 
                        >gmm</li>
                    </ul>
                </div>
            </div>
            <div className='form-check-reverse mb-3'>
                <label className='form-check-label text-align-baseline' htmlFor='area-to-use'>Area Value to Use:</label>
                <div class="dropdown">
                    <button className="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                        {areaToUse}
                    </button>
                    <ul className="dropdown-menu">
                        <li className="dropdown-item" value="area_horizontal_raw"
                            onClick={(e) => {
                                setAreaToUse(e.target.innerText)
                            }}
                        >area_horizontal_raw</li>
                        <li className="dropdown-item" value="area_vertical_raw"
                            onClick={(e) => {
                                // console.log("e.target.value", e.target.innerText)
                                setAreaToUse(e.target.innerText)
                            }} 
                        >area_vertical_raw</li>
                    </ul>
                </div>
            </div> */}


                    {!!['DFI 1', 'DFI 2', 'DFI 3', 'DFI 4'].includes(selectedPanel) &&
                        <div className='kit-reg-section'>
                            <h6 className='kit-reg-section-title'>
                                <i className='bi bi-clock me-2'></i>
                                Retention Time Range (mins)
                            </h6>
                            <div className='kit-reg-grid' style={{ gridTemplateColumns: '1fr 1fr' }}>
                                <div className='kit-reg-field'>
                                    <label className='kit-reg-label'>Minimum</label>
                                    <input type="number" className="kit-reg-input" aria-label="retention time min"
                                        min={0}
                                        value={rtMin}
                                        onChange={(e) => setRtMin(e.target.value)}
                                        step={0.1}
                                    />
                                </div>
                                <div className='kit-reg-field'>
                                    <label className='kit-reg-label'>Maximum</label>
                                    <input type="number" className="kit-reg-input" aria-label="retention time max"
                                        min={0}
                                        value={rtMax}
                                        onChange={(e) => setRtMax(e.target.value)}
                                        step={0.1}
                                    />
                                </div>
                            </div>
                        </div>
                    }

                    {/* STEP 2: Upload Data */}
                    <div className='kit-reg-section'>
                        <h6 className='kit-reg-section-title'>
                            <i className='bi bi-2-circle me-2'></i>
                            Upload Data Files (.zip)
                        </h6>

                        <div
                            ref={dropZoneRef}
                            className={`custom-file-drop ${isDragOver ? 'drag-over' : ''} ${zipFile ? 'has-file' : ''}`}
                            style={{ minHeight: '160px', position: 'relative', borderRadius: '6px' }}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDragOver(false);
                                const files = e.dataTransfer.files;
                                if (files.length > 0) handleFileSelected(files[0]);
                            }}
                        >
                            <input
                                type="file"
                                accept="application/zip, application/x-zip-compressed, multipart/x-zip, application/vnd.rar, application/x-rar-compressed, application/octet-stream"
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 2 }}
                                onChange={(e) => {
                                    if (e.target.files.length > 0) handleFileSelected(e.target.files[0]);
                                }}
                            />
                            <span className="drop-text" style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center', zIndex: 1 }}>
                                <i className={`bi ${zipFile ? 'bi-file-earmark-check' : 'bi-cloud-arrow-up'} me-2`}></i>
                                <span>{zipFile ? zipFile.name : 'Drag & drop your zipped experiment data or click to browse'}</span>
                                {zipFile && (
                                    <i
                                        className="bi bi-x-circle ms-2"
                                        style={{ pointerEvents: 'auto', cursor: 'pointer', color: '#c0392b', zIndex: 3 }}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setZipFile(null); }}
                                    ></i>
                                )}
                            </span>
                        </div>

                        {
                            uploadProgress > 0 &&
                            <div className='mt-3'>
                                <div className='d-flex flex-row justify-content-between align-items-center mb-1'>
                                    <span className='kit-reg-label mb-0'>Upload Progress</span>
                                    <span className='kit-reg-label mb-0'>{uploadProgress.toFixed(0)}%&nbsp;{uploadProgress === 100 ? <i className="bi bi-check-circle-fill text-success"></i> : <span className="spinner-border spinner-border-sm"></span>}</span>
                                </div>
                                <div className='progress' style={{ height: '6px' }}>
                                    <div className='progress-bar' role='progressbar' style={{ width: `${uploadProgress}%`, background: 'linear-gradient(135deg, #1164B5 0%, #0C99BA 100%)' }} />
                                </div>
                            </div>
                        }
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className='kit-reg-actions'>
                        <button className='btn-lc-small btn-lc-small-secondary flex-grow-1' id='reset-submission-btn'
                            onClick={() => { window.location.reload() }}
                        >
                            <i className="bi bi-arrow-counterclockwise me-1"></i> Reset
                        </button>
                        <button className='btn-lc-small flex-grow-1'
                            disabled={
                                !zipFile ||
                                (uploadProgress > 0 && uploadProgress < 100) ||
                                parsingFiles ||
                                fileFlags.length > 0 ||
                                unacceptedFiles.length > 0 ||
                                (!rtFile && ["Panel A", "Panel B"].includes(selectedPanel))
                            }
                            onClick={(e) => {
                                confirmSubmission()
                                e.target.disabled = true
                                document.getElementById('reset-submission-btn').disabled = true
                                e.target.innerHTML = ""
                                e.target.innerText = 'Confirming Submission...'
                                e.target.innerHTML += '<span class="spinner-border spinner-border-sm mx-1" role="status" aria-hidden="true"></span>'
                            }}
                        >
                            <i className="bi bi-check me-1"></i> Submit
                        </button>
                    </div>

                </div>
            </div>{/* end sub-form-side */}

            {/* RIGHT COLUMN: Files Found */}
            <div className='sub-files-side'>
                <div className='kit-reg-card'>
                    <div className='kit-reg-section mb-0 border-0'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                            <h6 className='kit-reg-section-title mb-0'>
                                <i className='bi bi-table me-2'></i>
                                Files Found
                            </h6>
                            {
                                uploadProgress === 100 && parsingFiles ?
                                    <span className='d-inline-flex align-items-center gap-2' style={{ fontSize: '0.82rem', color: '#757575' }}>
                                        Validating...
                                        <span className="spinner-border spinner-border-sm"></span>
                                    </span> : null
                            }
                        </div>
                        <div className='submission-files-grid d-flex flex-column gap-2'>
                            {
                                !!!['DFI 1', 'DFI 2', 'DFI 3', 'DFI 4'].includes(selectedPanel) &&
                                <div className='file-category-group'>
                                    <div className={`file-category-header ${rtFile ? 'status-success' : 'status-danger'}`}>
                                        <span>Retention Time Template</span>
                                        <span className='file-category-count'>{rtFile ? '1' : '0'}</span>
                                    </div>
                                    <div className='file-category-items'>
                                        {rtFile ?
                                            <span className='file-item-pill' key={`parsed-${rtFile}`}>{rtFile}</span>
                                            : null
                                        }
                                    </div>
                                </div>
                            }

                            <div className='file-category-group'>
                                <div className={`file-category-header ${doubleBlankFiles.length ? doubleBlankFiles.length === FILE_VALIDATIONS["DFI 1"].counts.double_blank ? 'status-success' : 'status-warning' : 'status-danger'}`}>
                                    <span>Double Blank</span>
                                    <span className='file-category-count'>{doubleBlankFiles.length}/{FILE_VALIDATIONS["DFI 1"].counts.double_blank}</span>
                                </div>
                                <div className='file-category-items'>
                                    {doubleBlankFiles.map((fileName) => (
                                        <span className='file-item-pill' key={`parsed-${fileName}`}>{fileName}</span>
                                    ))}
                                </div>
                            </div>
                            <div className='file-category-group'>
                                <div className={`file-category-header ${blankFiles.length ? blankFiles.length === FILE_VALIDATIONS["DFI 1"].counts.blank ? 'status-success' : 'status-warning' : 'status-danger'}`}>
                                    <span>Blank</span>
                                    <span className='file-category-count'>{blankFiles.length}/{FILE_VALIDATIONS["DFI 1"].counts.blank}</span>
                                </div>
                                <div className='file-category-items'>
                                    {blankFiles.map((fileName) => (
                                        <span className='file-item-pill' key={`parsed-${fileName}`}>{fileName}</span>
                                    ))}
                                </div>
                            </div>
                            <div className='file-category-group'>
                                <div className={`file-category-header ${calFiles.length ? calFiles.length === FILE_VALIDATIONS["DFI 1"].counts.cal ? 'status-success' : 'status-warning' : 'status-danger'}`}>
                                    <span>Calibration</span>
                                    <span className='file-category-count'>{calFiles.length}/{FILE_VALIDATIONS["DFI 1"].counts.cal}</span>
                                </div>
                                <div className='file-category-items'>
                                    {calFiles.map((fileName) => (
                                        <span className='file-item-pill' key={`parsed-${fileName}`}>{fileName}</span>
                                    ))}
                                </div>
                            </div>
                            <div className='file-category-group'>
                                <div className={`file-category-header ${qcFiles.length ? qcFiles.length === FILE_VALIDATIONS["DFI 1"].counts.qc ? 'status-success' : 'status-warning' : 'status-danger'}`}>
                                    <span>Quality Control</span>
                                    <span className='file-category-count'>{qcFiles.length}/{FILE_VALIDATIONS["DFI 1"].counts.qc}</span>
                                </div>
                                <div className='file-category-items'>
                                    {qcFiles.map((fileName) => (
                                        <span className='file-item-pill' key={`parsed-${fileName}`}>{fileName}</span>
                                    ))}
                                </div>
                            </div>
                            <div className='file-category-group'>
                                <div className='file-category-header status-info'>
                                    <span>Experimental</span>
                                    <span className='file-category-count'>{experimentalFiles.length}</span>
                                </div>
                                <div className='file-category-items'>
                                    {experimentalFiles.map((fileName) => (
                                        <span className='file-item-pill' key={`parsed-${fileName}`}>{fileName}</span>
                                    ))}
                                </div>
                            </div>
                            <div className='file-category-group'>
                                <div className={`file-category-header ${unacceptedFiles.length === 0 ? 'status-success' : 'status-danger'}`}>
                                    <span>Unacceptable Files</span>
                                    <span className='file-category-count'>{unacceptedFiles.length}</span>
                                </div>
                                {
                                    fileFlags.length > 0 &&
                                    <div className='d-flex flex-wrap gap-1 mt-1'>
                                        {
                                            fileFlags.map((flag) => (
                                                <span className='file-flag-pill' key={`flag-${flag}`}>{flag}</span>
                                            ))
                                        }
                                    </div>
                                }
                                <div className='file-category-items'>
                                    {unacceptedFiles.map((fileName) => (
                                        <span className='file-item-pill file-item-danger' key={`parsed-${fileName}`}>{fileName}</span>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>{/* end sub-files-side */}

        </div>
    )
}




function validateFileName(name, PROJECT_ID, KIT_REGISTRATION_ID, isRTFile = false, instrument = null, biofluid = null, assay = null, panel = null) {
    // flags.length === 0 ? valid : invalid
    let flags = []
    let ext = name.split('.').pop()
    console.log(name, ext)
    if (isRTFile) {
        ext !== 'csv' ? flags.push('invalid file extension') : null
        expected_name = `LC_Autofit-${instrument}-panel-${panel.at(-1)}-${assay}-${biofluid.toLowerCase()}-RetentionTime-Template.csv`
        console.log("expected_name", expected_name)
        name !== expected_name ? flags.push(`invalid rt file name [${expected_name}]`) : null
    } else {
        // -- check if name is in the format of {PROJECT_ID}_{KIT_REGISTRATION_ID} and IDs are correct
        let pId = name.split('_')[0];
        let kId = name.split('_')[1];
        (pId !== PROJECT_ID || kId !== KIT_REGISTRATION_ID) ?
            flags.push('invalid project or kit registration id') : null
        ACCEPTED_DATA_FILE_EXTENSIONS.includes(ext) ? '' : flags.push('invalid file extension')
    }

    console.log(">>> flags", flags)
    return flags
}


function detectRTFile(names) {
    let rtFile = []
    names.forEach((name) => {
        // lowerCaseName = name.toLowerCase()
        console.log("detectRTFile", name)
        name.includes('RetentionTime-Template') ? rtFile.push(name) : null
    })

    return rtFile
}

function validateRTRows(rows) {
    rtFlags = []
    if (Object.keys(rows).length == 1) {
        let rtRow = rows[Object.keys(rows)[0]]
        console.log("rtRow", rtRow)
        if (rtRow.length > 0) {
            rtRow.forEach((row) => {
                analyteCode = row['Analyte']
                if (row['RT'] === '' || row['RT'] === null || row['RT'] === undefined) {
                    rtFlags.push(`RT value is empty for ${analyteCode}`)
                } else if (isNaN(row['RT'])) {
                    rtFlags.push(`RT value is not a number for ${analyteCode}`)
                } else if (row['RT'] < 0) {
                    rtFlags.push(`RT value cannot be negative for ${analyteCode}`)
                }
            })
        } else {
            rtFlags.push('No RT rows found')
        }
    } else {
        rtFlags.push('No or more than one RT template found')
    }
    return rtFlags
}

